import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import { useRoute, useRouter } from 'vue-router'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed, type Ref } from 'vue'

/**
 * Функция загрузки данных для вкладки конфигуратор
 */
export const useLoadTypesModelList = (objectId: Ref<string>) => {
  const http = useHttp()
  const url = computed(() => `/inventory/objects/${objectId.value}/object-types`)

  const queryKey = computed(() => ['load_types_model_list', objectId])

  return useQuery({
    queryKey,
    queryFn: () => http.get(url.value),
    retry: false,
  })
}

/**
 * Функция сохранения полей
 * @param objectId
 */
export const useSendForm = (objectId: string) => {
  const http = useHttp()
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()
  const router = useRouter()
  const route = useRoute()

  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (data.id) {
        return http.put(`/inventory/group/${objectId}/object-types/${data.id}`, data)
      } else {
        return http.post(`/inventory/group/${objectId}/object-types`, data)
      }
    },
  })

  const sendForm = async (
    data: any,
    valid: boolean,
    options?: { navigate?: boolean; notify?: boolean },
  ): Promise<boolean> => {
    if (!valid) return false
    const navigate = options?.navigate ?? true
    const notify = options?.notify ?? true

    try {
      data.invent_object_actions = data.invent_object_actions.map((item) => {
        item.count = Number(item.count)
        return item
      })

      const { data: objectType } = await mutation.mutateAsync(data)

      if (notify) addSuccess(trs('itf.saved'))

      if (navigate && objectType)
        await router.push({
          name: 'objects.groups.admin',
          params: {
            ...route.params,
            action: route.params.action === 'edit-v2' ? 'edit-v2' : 'edit',
            entity_id: objectType,
          },
        })
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    }
  }

  return {
    ...mutation,

    sendForm,
  }
}

/** Легаси-имя типа поля → код field_type нормализованного каталога. */
const LEGACY_TYPE_TO_FIELD_TYPE: Record<string, number> = {
  string: 21,
  date: 22,
  range: 23,
  number: 24,
  boolean: 25,
  year: 26,
  phone: 27,
  email: 28,
  interator_simple: 30,
  iterator: 30,
  computed: 31,
  address: 32,
  list_types: 10,
  catalog: 10,
}

/** Числовой id каталога или null, если ключ ещё легаси-строка (новое поле формы). */
const catalogFieldId = (field: any): number | null => {
  const id = Number(field?.id)
  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * Поле, которым карточка вправе управлять: своё (не унаследованное) и с типом,
 * который отображается в каталог. Связи пропускаем намеренно — их парные
 * зеркала ведёт бэкенд, и правка через общий список полей их рассинхронит.
 */
const isManagedField = (field: any): boolean =>
  !field?.inherited && LEGACY_TYPE_TO_FIELD_TYPE[String(field?.type)] != null

const fieldPayload = (typeId: number, field: any) => {
  const fieldType = LEGACY_TYPE_TO_FIELD_TYPE[String(field?.type)] ?? 21

  return {
    catalog_objects_type_id: typeId,
    field_type: fieldType,
    name: String(field?.name ?? '').trim(),
    catalog_id: fieldType === 10 ? Number(field?.object_type) || null : null,
    reference_id: null,
    mirror_field_id: null,
    default_value: field?.default_value == null ? '' : String(field.default_value),
    is_system: Boolean(field?.is_system),
    is_required: Boolean(field?.mandatory),
    use_in_filter: Boolean(field?.is_filtered),
    use_in_search: Boolean(field?.is_global_search),
    validation_props: {
      items: field?.items ?? [],
      is_table: Boolean(field?.is_table),
      multiple: Boolean(field?.multiple),
      count_object: Boolean(field?.count_object),
    },
    sort: Number(field?.sort) || 0,
  }
}

/**
 * Синхронизация определений полей типа с нормализованным каталогом.
 *
 * Карточка ведёт поля списком в памяти: добавление — push со строковым ключом,
 * удаление — splice, и всё уезжало общим PUT типа. Поля из его тела бэкенд
 * больше не читает, поэтому правки сохраняем поштучно через /object-types/:id/fields:
 * поле без числового id — новое, пропавшее из списка — удалённое, изменившееся —
 * обновлённое.
 */
export const useSyncObjectTypeFields = () => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  const syncFields = async (
    typeId: number,
    currentFields: any[],
    initialFields: any[] | null,
  ): Promise<boolean> => {
    // Снимок исходного состояния не загружен — сравнивать не с чем, а разница
    // «пусто против непусто» была бы прочитана как удаление всех полей.
    if (!typeId || initialFields == null) return true

    const current = (currentFields ?? []).filter(isManagedField)
    const initial = (initialFields ?? []).filter(isManagedField)

    const initialById = new Map<number, any>()
    initial.forEach((field) => {
      const id = catalogFieldId(field)
      if (id) initialById.set(id, field)
    })

    const kept = new Set<number>()

    try {
      for (const field of current) {
        const id = catalogFieldId(field)
        const payload = fieldPayload(typeId, field)

        if (id == null) {
          await http.post(`/object-types/${typeId}/fields`, payload)
          continue
        }

        const before = initialById.get(id)
        if (!before) continue

        kept.add(id)
        if (JSON.stringify(fieldPayload(typeId, before)) !== JSON.stringify(payload)) {
          await http.put(`/object-types/${typeId}/fields/${id}`, payload)
        }
      }

      for (const id of initialById.keys()) {
        if (!kept.has(id)) {
          await http.del(`/object-types/${typeId}/fields/${id}`)
        }
      }

      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    }
  }

  return { syncFields }
}

/**
 * Сохранение значений записи справочника.
 *
 * У записи правятся не определения полей — они принадлежат корню справочника, —
 * а значения. Общий PUT типа их не сохраняет: поля из его тела бэкенд не читает,
 * значения живут в отдельной модели и адресуются числовым id поля из каталога.
 *
 * Легаси-карточка держит значение записи в `default_value` поля, оттуда и
 * собираем. Поля со строковыми ключами (наследие JSONB) пропускаем — по такому
 * ключу значение не адресуется.
 */
export const useSaveDictionaryEntryValues = () => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: ({ entryId, values }: { entryId: number; values: unknown[] }) =>
      http.put(`/dictionaries/${entryId}/passport`, { values }),
  })

  const saveValues = async (entryId: number, fields: any[]): Promise<boolean> => {
    const values = (fields ?? [])
      // Только унаследованные: собственное поле записи хранит своё значение в
      // определении, и дублировать его отдельной строкой значений незачем.
      .filter((field: any) => field?.inherited)
      .map((field: any) => ({
        field_id: Number(field?.id),
        value: field?.default_value == null ? '' : String(field.default_value),
      }))
      .filter((value) => Number.isInteger(value.field_id) && value.field_id > 0)

    if (!values.length || !entryId) return true

    try {
      await mutation.mutateAsync({ entryId, values })
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    }
  }

  return {
    ...mutation,

    saveValues,
  }
}

/**
 * Функция смены сортировки групп
 */
export const useReorderGroup = (refetch: Function) => {
  const http = useHttp()
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: (data: any) => {
      return http.put(`/manuals/object-types/group/reorder`, data)
    },
  })

  const reorder = async (data: any) => {
    try {
      await mutation.mutateAsync(data)
      await refetch()

      addSuccess(trs('itf.saved'))
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
    }
  }

  return {
    ...mutation,

    reorder,
  }
}
